/**
 * Shared utilities for the placeholder pills rendered inside the TinyMCE
 * editors: type detection (variable / AI snippet / spintax), the
 * token-to-span decoration used in `BeforeSetContent`, and the pill CSS
 * (colors + icon per type) for `content_style`.
 */
import { faBolt, faShuffle, faTag, IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import { CustomColumnTypes } from "@/enum/list.enum";

import { isValidPlaceholder } from "./placeholderValidation";

export type PlaceholderPillType = "variable" | "ai" | "spintax";

/** Minimal column shape needed to classify a `column_<id>_…` placeholder. */
interface PillColumn {
	id?: number | null;
	type?: CustomColumnTypes | string;
}

const COLUMN_KEY_REGEX = /^column_(\d+)_/;

/**
 * Classifies a placeholder key: `column_<id>_…` keys are looked up in the
 * list columns — AI_SNIPPET → "ai", RANDOM_VALUE → "spintax" — everything
 * else (other column types, prospect/sender keys, unknown ids) is a plain
 * "variable".
 */
export const getPlaceholderPillType = (
	varName: string,
	columns?: PillColumn[]
): PlaceholderPillType => {
	const match = COLUMN_KEY_REGEX.exec(varName);

	if (!match) {
		return "variable";
	}

	const column = columns?.find((c) => c.id === Number(match[1]));

	if (column?.type === CustomColumnTypes.AI_SNIPPET) {
		return "ai";
	}

	if (column?.type === CustomColumnTypes.RANDOM_VALUE) {
		return "spintax";
	}

	return "variable";
};

interface DecoratePlaceholderTokensOptions {
	/** Suggestions the editor accepts — used to flag unknown placeholders. */
	processedSuggestions: { text: string; value: string }[];
	/** List columns used to classify `column_<id>_…` pills. */
	columns?: PillColumn[];
}

/**
 * Turns every `{varName|label}` token of an editor content string into the
 * non-editable placeholder span TinyMCE renders as a pill. Used by the
 * editors' `BeforeSetContent` handler (`GetContent` reverses it).
 */
export const decoratePlaceholderTokens = (
	content: string,
	{ processedSuggestions, columns }: DecoratePlaceholderTokensOptions
): string =>
	content.replace(/\{([^}]+)\}/g, (_match: string, varName: string) => {
		const [currentVarName, currentVarLabel] = varName.split("|");

		let label = currentVarName.split("_")[currentVarName.split("_").length - 1];

		if (currentVarLabel && currentVarLabel.length > 0) {
			label = currentVarLabel;
		}

		if (currentVarName.includes("column_")) {
			label = currentVarName.split("_")[currentVarName.split("_").length - 1];
		}

		const pillType = getPlaceholderPillType(currentVarName, columns);
		const valid = isValidPlaceholder(currentVarName, processedSuggestions);
		const invalidAttrs = valid ? "" : ` data-invalid="true" title="This variable does not exist"`;

		return `<span data-placeholder="${currentVarName}|${
			currentVarLabel || currentVarName
		}" data-pill-type="${pillType}" class="mceNonEditable" contenteditable="false" data-mce-noneditable="true"${invalidAttrs}>${label}</span>`;
	});

/** Builds a CSS mask url() from a FontAwesome icon so it tints via currentColor. */
const iconMaskUrl = ({ icon }: IconDefinition): string => {
	const [width, height, , , pathData] = icon;
	const paths = (Array.isArray(pathData) ? pathData : [pathData])
		.map((d) => `<path d='${d}'/>`)
		.join("");
	const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'>${paths}</svg>`;

	return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
};

const VARIABLE_ICON = iconMaskUrl(faTag);
const AI_ICON = iconMaskUrl(faBolt);
const SPINTAX_ICON = iconMaskUrl(faShuffle);

/**
 * Pill CSS for `content_style`: a neutral base for plain variables plus the
 * AI snippet (blue) and spintax (purple) variants, each with its icon.
 * Append `invalidPlaceholderStyle` after it so invalid pills win.
 */
export const placeholderPillStyle = `
	span[data-placeholder] {
		background: #EEF2F6;
		border: 1px solid #CDD5DF;
		border-radius: 999px;
		padding: 2px 6px;
		font-size: 0.9em;
		color: #364152;
		font-weight: 500;
		white-space: nowrap;
	}
	span[data-placeholder]::before {
		content: "";
		display: inline-block;
		width: 10px;
		height: 10px;
		margin-right: 4px;
		vertical-align: -1px;
		background-color: currentColor;
		-webkit-mask: ${VARIABLE_ICON} no-repeat center / contain;
		mask: ${VARIABLE_ICON} no-repeat center / contain;
	}
	span[data-placeholder][data-pill-type="ai"] {
		background: #EEF1FE;
		border-color: #C5D0FB;
		color: #4F6EF7;
		font-weight: 600;
	}
	span[data-placeholder][data-pill-type="ai"]::before {
		-webkit-mask-image: ${AI_ICON};
		mask-image: ${AI_ICON};
	}
	span[data-placeholder][data-pill-type="spintax"] {
		background: #F4EFFD;
		border-color: #E0D2FB;
		color: #6938EF;
	}
	span[data-placeholder][data-pill-type="spintax"]::before {
		-webkit-mask-image: ${SPINTAX_ICON};
		mask-image: ${SPINTAX_ICON};
	}
`;
